import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, X, ChevronLeft, ChevronRight, Grid3X3, LayoutGrid } from 'lucide-react';
import Layout from '../components/layout/Layout';
import { portfolioProjects, portfolioImages } from '../data/mock';

const GalleryPage = () => {
  const [activeFilter, setActiveFilter] = useState('All');
  const [selectedProject, setSelectedProject] = useState(null);
  const [viewMode, setViewMode] = useState('grid');

  const filters = ['All', 'Living Room', 'Bedroom', 'Kitchen', 'Office'];

  const filteredProjects = activeFilter === 'All'
    ? portfolioProjects
    : portfolioProjects.filter(project => project.type === activeFilter);

  const openLightbox = (project) => {
    setSelectedProject(project);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setSelectedProject(null);
    document.body.style.overflow = 'auto';
  };

  const navigateProject = (direction) => {
    const currentIndex = filteredProjects.findIndex(p => p.id === selectedProject.id);
    let newIndex;
    if (direction === 'next') {
      newIndex = currentIndex === filteredProjects.length - 1 ? 0 : currentIndex + 1;
    } else {
      newIndex = currentIndex === 0 ? filteredProjects.length - 1 : currentIndex - 1;
    }
    setSelectedProject(filteredProjects[newIndex]);
  };

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-24 bg-[#2C2C2C] overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img
            src={portfolioImages.hero[0]}
            alt="Gallery Background"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-[#2C2C2C] via-[#2C2C2C]/95 to-[#2C2C2C]" />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
          <div className="max-w-3xl">
            <span className="inline-block text-[#B87333] text-sm tracking-[0.2em] uppercase mb-4 animate-fade-in">
              Our Portfolio
            </span>
            <h1 className="font-serif text-4xl lg:text-5xl xl:text-6xl text-white mb-6 leading-tight animate-fade-in-up">
              Spaces We've
              <span className="block text-[#D4956A]">Transformed</span>
            </h1>
            <p className="text-white/70 text-lg leading-relaxed animate-fade-in-up delay-200">
              Explore our collection of thoughtfully designed interiors, each 
              telling a unique story of style, comfort, and functionality.
            </p>
          </div>
        </div>
      </section>

      {/* Filter Section */}
      <section className="py-8 bg-[#FDFBF7] sticky top-[72px] z-40 border-b border-[#E8E3DB]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
            {/* Filters */}
            <div className="flex flex-wrap justify-center gap-2 sm:gap-4">
              {filters.map((filter) => (
                <button
                  key={filter}
                  onClick={() => setActiveFilter(filter)}
                  className={`px-4 py-2 text-sm tracking-wider uppercase transition-all duration-300 ${
                    activeFilter === filter
                      ? 'bg-[#B87333] text-white'
                      : 'bg-transparent text-[#6B6B6B] hover:text-[#B87333] hover:bg-[#B87333]/5'
                  }`}
                >
                  {filter}
                </button>
              ))}
            </div>

            {/* View Mode Toggle */}
            <div className="flex items-center gap-2 border border-[#E8E3DB] p-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 transition-colors ${
                  viewMode === 'grid' ? 'bg-[#B87333] text-white' : 'text-[#6B6B6B] hover:text-[#B87333]'
                }`}
                aria-label="Grid view"
              >
                <Grid3X3 size={18} />
              </button>
              <button
                onClick={() => setViewMode('masonry')}
                className={`p-2 transition-colors ${
                  viewMode === 'masonry' ? 'bg-[#B87333] text-white' : 'text-[#6B6B6B] hover:text-[#B87333]'
                }`}
                aria-label="Masonry view"
              >
                <LayoutGrid size={18} />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-16 lg:py-24 bg-[#F5F0E8]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {/* Results Count */}
          <div className="mb-8 text-[#6B6B6B] text-sm">
            Showing {filteredProjects.length} projects
            {activeFilter !== 'All' && ` in ${activeFilter}`}
          </div>

          {/* Grid View */}
          {viewMode === 'grid' && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {filteredProjects.map((project, index) => (
                <div
                  key={project.id}
                  className="group cursor-pointer"
                  onClick={() => openLightbox(project)}
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div className="relative aspect-[4/3] overflow-hidden bg-[#E8E3DB]">
                    <img
                      src={project.image}
                      alt={project.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#2C2C2C]/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    <div className="absolute inset-0 flex items-end p-6 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                      <div>
                        <span className="inline-block px-3 py-1 bg-[#B87333]/80 text-white text-xs tracking-wider uppercase mb-2">
                          {project.type}
                        </span>
                        <h3 className="font-serif text-xl text-white">
                          {project.title}
                        </h3>
                      </div>
                    </div>
                  </div>
                  <div className="pt-4">
                    <span className="text-[#B87333] text-xs tracking-wider uppercase">
                      {project.category}
                    </span>
                    <h3 className="font-serif text-lg text-[#2C2C2C] group-hover:text-[#B87333] transition-colors">
                      {project.title}
                    </h3>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Masonry View */}
          {viewMode === 'masonry' && (
            <div className="columns-1 md:columns-2 lg:columns-3 gap-6">
              {filteredProjects.map((project, index) => (
                <div
                  key={project.id}
                  className="group cursor-pointer mb-6 break-inside-avoid"
                  onClick={() => openLightbox(project)}
                >
                  <div className={`relative overflow-hidden bg-[#E8E3DB] ${
                    index % 3 === 0 ? 'aspect-[3/4]' : index % 3 === 1 ? 'aspect-square' : 'aspect-[4/3]'
                  }`}>
                    <img
                      src={project.image}
                      alt={project.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#2C2C2C]/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    <div className="absolute inset-0 flex items-end p-6 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                      <div>
                        <span className="inline-block px-3 py-1 bg-[#B87333]/80 text-white text-xs tracking-wider uppercase mb-2">
                          {project.type}
                        </span>
                        <h3 className="font-serif text-xl text-white">
                          {project.title}
                        </h3>
                        <p className="text-white/70 text-sm mt-2">
                          {project.description}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Lightbox */}
      {selectedProject && (
        <div className="fixed inset-0 z-50 bg-[#1a1a1a]/95 flex items-center justify-center">
          {/* Close button */}
          <button
            onClick={closeLightbox}
            className="absolute top-6 right-6 p-3 text-white/60 hover:text-white transition-colors z-10"
            aria-label="Close lightbox"
          >
            <X size={28} />
          </button>

          {/* Navigation */}
          <button
            onClick={() => navigateProject('prev')}
            className="absolute left-6 top-1/2 -translate-y-1/2 p-3 text-white/60 hover:text-white transition-colors z-10"
            aria-label="Previous project"
          >
            <ChevronLeft size={40} />
          </button>
          <button
            onClick={() => navigateProject('next')}
            className="absolute right-6 top-1/2 -translate-y-1/2 p-3 text-white/60 hover:text-white transition-colors z-10"
            aria-label="Next project"
          >
            <ChevronRight size={40} />
          </button>

          {/* Content */}
          <div className="max-w-5xl mx-auto px-16">
            <div className="relative">
              <img
                src={selectedProject.image}
                alt={selectedProject.title}
                className="w-full max-h-[70vh] object-contain"
              />
            </div>
            <div className="mt-6 text-center">
              <span className="inline-block px-3 py-1 bg-[#B87333]/20 text-[#B87333] text-xs tracking-wider uppercase mb-3">
                {selectedProject.type} • {selectedProject.category}
              </span>
              <h3 className="font-serif text-3xl text-white mb-2">
                {selectedProject.title}
              </h3>
              <p className="text-white/60">
                {selectedProject.description}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* CTA Section */}
      <section className="py-24 lg:py-32 bg-[#FDFBF7]">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <span className="inline-block text-[#B87333] text-sm tracking-[0.2em] uppercase mb-4">
            Inspired?
          </span>
          <h2 className="font-serif text-4xl lg:text-5xl text-[#2C2C2C] mb-6">
            Let's Create Your
            <span className="block text-[#B87333]">Dream Space</span>
          </h2>
          <p className="text-[#6B6B6B] text-lg mb-10 max-w-2xl mx-auto">
            Your unique vision deserves a one-of-a-kind design. Let's collaborate 
            to bring your dream space to life.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center px-10 py-5 bg-[#B87333] text-white text-sm tracking-wider uppercase hover:bg-[#8B5A2B] transition-all duration-300 hover:-translate-y-0.5 group"
          >
            Start Your Project
            <ArrowRight size={16} className="ml-3 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </section>
    </Layout>
  );
};

export default GalleryPage;
